import React, { useState, useEffect, Suspense } from "react";
import axios from "axios";
import "./JoinMovementForm.css";

const FooterNew = React.lazy(() =>
  import("../FooterNewChanges/FooterNewChanges")
);

function JoinMovementForm() {
  const [validated, setValidated] = useState(false);
  const [formLoading, setFormLoading] = useState(false);
  const [leadNo, setLeadNo] = useState("");
  const [email, setEmail] = useState("");
  const [name, setName] = useState("");
  const [phone, setPhone] = useState("");
  const [consentValue, setConsentValue] = useState(true);
  const [showSuccessModal, setShowSuccessModal] = useState(false);
  const [showFailModal, setShowFailModal] = useState(false);
  const [consentData, setConsentData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  
  // Validation error states
  const [emailError, setEmailError] = useState("");
  const [phoneError, setPhoneError] = useState("");
  const [nameError, setNameError] = useState("");

  const queryParams = new URLSearchParams(window.location.search);
  const utmSource = queryParams.get("utm_source") || undefined;
  const utmMedium = queryParams.get("utm_medium") || undefined;
  const utmCampaign = queryParams.get("utm_campaign") || undefined;
  const utmContent = queryParams.get("utm_content") || undefined;

  const domainName = 'angeltwin.com';   
  const language = 'en';

  // Validation patterns
  const emailPattern = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
  const phonePattern = /^\(\d{3}\) \d{3}-\d{4}$/; // Matches (123) 456-7890 format

  const fetchConsentData = async () => {
    try {
      const response = await fetch(process.env.REACT_APP_LEAD_GENERATION_CONSENT_JSON);
      const data = await response.json();

      const selectedDomain = data[domainName] ? domainName : 'swmc.com';  
      const domainData = data[selectedDomain];   

      const consent = {
        submitButtonConsent: domainData.submit_button_consent[language],   
        automatedPhoneSolicitationConsent: domainData.automated_phone_solicitation_consent[language],   
        version: domainData.version,  
      };
  
      setConsentData(consent);
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };
  
  useEffect(() => {
    fetchConsentData();
  }, []);

  const handleFailClose = () => {
    setShowFailModal(false);
    setValidated(false);
  };

  const handleSuccessClose = () => {
    setShowSuccessModal(false);
    setValidated(false);
  };

  const clearForm = () => {
    setLeadNo("");
    setName("");
    setEmail("");
    setPhone("");
    setConsentValue(true);
    // Clear validation errors
    setEmailError("");
    setPhoneError("");
    setNameError("");
  };

  // Phone number validation and formatting
  const handleTelephoneNumber = (evt) => {
    let tmpVal = evt.target.value.replace(/[^\d]/g, "");
    let formattedNumber = "";
    
    if (tmpVal.length > 0) {
      if (tmpVal.length <= 3) {
        formattedNumber = tmpVal;
      } else if (tmpVal.length <= 6) {
        formattedNumber = `(${tmpVal.slice(0, 3)}) ${tmpVal.slice(3)}`;
      } else {
        formattedNumber = `(${tmpVal.slice(0, 3)}) ${tmpVal.slice(3, 6)}-${tmpVal.slice(6, 10)}`;
      }
    }
    
    setPhone(formattedNumber);
    
    // Real-time validation for phone
    if (formattedNumber) {
      const error = validatePhone(formattedNumber);
      setPhoneError(error);
    } else {
      setPhoneError("");
    }
  };

  // Email validation
  const validateEmail = (email) => {
    if (!email) return "";
    if (!emailPattern.test(email)) {
      return "Please enter a valid email address.";
    }
    return "";
  };

  // Phone validation - check for exactly 10 digits
  const validatePhone = (phone) => {
    if (!phone) return "";
    
    // Extract only digits from the formatted phone number
    const digitsOnly = phone.replace(/[^\d]/g, "");
    
    if (digitsOnly.length < 10) {
      return "Please enter a complete 10-digit phone number.";
    } else if (digitsOnly.length > 10) {
      return "Phone number should be exactly 10 digits.";
    } else if (!phonePattern.test(phone)) {
      return "Please enter a valid phone number in format (123) 456-7890.";
    }
    return "";
  };

  // Name validation
  const validateName = (name) => {
    if (!name || name.trim().length < 2) {
      return "Please enter your name.";
    }
    return "";
  };

  // Handle email change with real-time validation
  const handleEmailChange = (e) => {
    const value = e.target.value;
    setEmail(value);
    
    // Real-time validation
    if (value) {
      const error = validateEmail(value);
      setEmailError(error);
    } else {
      setEmailError("");
    }
  };

  // Handle name change with real-time validation
  const handleNameChange = (e) => {
    const value = e.target.value;
    setName(value);
    
    // Real-time validation
    if (value) {
      const error = validateName(value);
      setNameError(error);
    } else {
      setNameError("");
    }
  };

  function getFormURL() {
    if (
      window.location.href.split("/")[3] &&
      window.location.href.split("/")[4]
    ) {
      const temp = window.location.href.split("/")[4].replace("#", "");
      return "/" + window.location.href.split("/")[3] + "/" + temp;
    } else {
      return "/";
    }
  }

  const handleSubmit = (event) => {
    event.preventDefault();
    const form = event.currentTarget;
    
    // Validate all fields
    const nameValidationError = validateName(name);
    const emailValidationError = validateEmail(email);
    const phoneValidationError = validatePhone(phone);
    
    // Set validation errors
    setNameError(nameValidationError);
    setEmailError(emailValidationError);
    setPhoneError(phoneValidationError);
    
    // Check if at least one contact method is provided and valid
    const hasValidEmail = email && !emailValidationError;
    const hasValidPhone = phone && !phoneValidationError;
    
    // Form is invalid if:
    // 1. Name is invalid
    // 2. Neither email nor phone is provided OR both are provided but invalid
    if (nameValidationError || (!hasValidEmail && !hasValidPhone)) {
      setValidated(true);
      
      // Set specific error messages for contact fields
      if (!email && !phone) {
        setEmailError("Please provide either an email or phone number.");
        setPhoneError("Please provide either an email or phone number.");
      }
      
      return;
    }
    
    setFormLoading(true);
    
    let firstName, lastName;
    if (name.includes(" ")) {
      const nameParts = name.split(" ");
      firstName = nameParts[0];
      lastName = nameParts.slice(1).join(" ");
    } else {
      firstName = name;
      lastName = "";
    }
    
    const phoneNumber = phone;
    const emailAddress = email;
    const comments = "";
    let formUrl = getFormURL();
    const consent_version = consentData ? consentData.version : undefined;
    const consent_version_text = consentData ? consentData.automatedPhoneSolicitationConsent.content : undefined;
    
    axios
      .post(process.env.REACT_APP_API_EMAIL, {
        firstName: firstName,
        lastName: lastName,
        phone: phoneNumber,
        email: emailAddress,
        companyName: "",
        referrerSource: "myangl.ai",
        comments: comments,
        utmSource: utmSource,
        utmMedium: utmMedium,
        utmCampaign: utmCampaign,
        utmContent: utmContent,
        formUrl: formUrl,
        leadSource: "ANGELTWIN",
        TCPA_Consent_Given: consentValue,
        consentVersion: consent_version,
        consentVersionText: consent_version_text,
      })
      .then((res) => {
        setLeadNo(res.data.success.brokerId);
        setShowSuccessModal(true);
        clearForm();
        form.reset();
        setValidated(false);
        setFormLoading(false);
      })
      .catch(function (error) {
        setShowFailModal(true);
        clearForm();
        form.reset();
        setValidated(false);
        setFormLoading(false);
      });
  };

  return (
    <>
    <div className="ai-movement-container bg-db-gr-rad">
      <div className="ai-movement-inner">
        <div className="ai-movement-header">
          <h1 className="">Request more <br></br>information about ANGL</h1>
        </div>
        
        <div className="ai-form-container">
          <form id="aiMovementForm" onSubmit={handleSubmit} noValidate className={validated ? "was-validated" : ""}>
            <div className="form-group">
              <label htmlFor="name">Name</label>
              <input 
                type="text" 
                id="name" 
                className={`form-control ${nameError ? 'is-invalid' : name && !nameError ? 'is-valid' : ''}`}
                value={name} 
                onChange={handleNameChange}
                required
              />
              {nameError && <div className="invalid-feedback">{nameError}</div>}
            </div>
            
            <div className="form-group">
              <label htmlFor="email">Email</label>
              <input 
                type="email" 
                id="email" 
                className={`form-control ${emailError ? 'is-invalid' : email && !emailError && emailPattern.test(email) ? 'is-valid' : ''}`}
                value={email} 
                onChange={handleEmailChange}
                required={!phone}
              />
              {emailError && <div className="invalid-feedback">{emailError}</div>}
            </div>
            
            <div className="form-group">
              <label htmlFor="phone">Phone</label>
              <input 
                type="tel" 
                id="phone" 
                className={`form-control ${phoneError ? 'is-invalid' : phone && !phoneError && phone.replace(/[^\d]/g, "").length === 10 ? 'is-valid' : ''}`}
                value={phone} 
                onChange={handleTelephoneNumber}
                placeholder=""
                required={!email}
              />
              {phoneError && <div className="invalid-feedback">{phoneError}</div>}
            </div>
            
            <div className="form-group">
              <button 
                type="submit" 
                className="submit-button" 
                disabled={formLoading}
              >
                {formLoading ? "Please wait..." : "Request more information"}
              </button>
            </div>
          </form>
        </div>
      </div>
      
      {showSuccessModal && (
        <div className="modal-overlay">
          <div className="modal-content-contactform">
            <div className="modal-header">
              <button className="close-button" onClick={handleSuccessClose}>×</button>
            </div>
            <div className="modal-body text-center">
              <p className="thank-text">Thank you for reaching out.<br></br>We will be in touch.</p>
            </div>
            <div className="modal-footer">
              <button onClick={handleSuccessClose}>Close</button>
            </div>
          </div>
        </div>
      )}
      
      {showFailModal && (
        <div className="modal-overlay">
          <div className="modal-content-contactform">
            <div className="modal-header">
              <button className="close-button" onClick={handleFailClose}>×</button>
            </div>
            <div className="modal-body">
              <p className="thank-text">
                Due to technical difficulties your request could not be
                processed. Please contact us at&nbsp;
                <a href={"mailto:inquiry@angeltwin.com"} className="form-link">
                  inquiry@angeltwin.com
                </a>.
              </p>
            </div>
            <div className="modal-footer">
              <button onClick={handleFailClose}>Close</button>
            </div>
          </div>
        </div>
      )}
    </div>

    <Suspense fallback={<div>Loading...</div>}>
      <FooterNew />
    </Suspense>
    </>
  );
}

export default JoinMovementForm;